/**
 * Application shell for Political Compass Trajectory.
 *
 * Owns the single source of truth (the entries array), persists it to
 * localStorage on every change and mirrors it into the URL fragment so a link
 * carries the whole trajectory. Editing lives in the chart and one dialog:
 * tapping open plot proposes a new point, tapping a dot opens it for editing and
 * dragging a dot moves it, and all three end in `PointDialog`. The list below the
 * chart is a read-only legend — close to, but not identical to, the exported one
 * — and the only keyboard route into the dialog. The one piece of derived data
 * is the chronological order.
 *
 * Initial state is resolved exactly once, lazily, in a `useState` initializer: a
 * share parameter in the URL wins over localStorage, a parameter that cannot be
 * read falls back to localStorage and says so once on the page, and no parameter
 * just loads localStorage. Resolving the URL (and only reading it) is pure, so a
 * `StrictMode` double render is harmless; the one write it implies — persisting
 * the shared entries so a reload keeps them — happens in a mount effect, because
 * persisting from inside the initializer would race React over whose array wins.
 *
 * Nothing here is announced by a banner: an action that can fail or take a moment
 * — Copy link, Download PNG — reports inside its own button, whose label is a
 * polite live region (see `Toolbar`). Clearing and deleting report nothing, and
 * the list changing is the feedback.
 */

import { useEffect, useRef, useState } from 'react'
import type { ReactElement } from 'react'

import CompassChart from './components/CompassChart'
import EntryList from './components/EntryList'
import type { EntryFocusTarget } from './components/EntryList'
import PointDialog from './components/PointDialog'
import type { PointDialogTarget, PointDialogValues } from './components/PointDialog'
import Toolbar from './components/Toolbar'
import type { CopyStatus, DownloadStatus } from './components/Toolbar'
import { isValidYear, sortChronologically } from './lib/compass'
import { downloadBlob, exportFilename, renderCompassToPng } from './lib/export'
import { PALETTE, QUADRANT_LABELS } from './lib/palette'
import type { QuadrantKey } from './lib/palette'
import { SHARE_PARAM, buildShareUrl, readShareFromHash } from './lib/share'
import { createEntry, loadEntries, saveEntries } from './lib/storage'
import type { Entry } from './lib/types'
import './styles/app.css'

/** Display order of the quadrant key: authoritarian first, left before right. */
const QUADRANT_ORDER: readonly QuadrantKey[] = ['authLeft', 'authRight', 'libLeft', 'libRight']

/**
 * How long edits settle before the URL fragment is rewritten, in milliseconds.
 * Exported so tests can advance the debounce with fake timers.
 */
export const SHARE_SYNC_DELAY = 300

/**
 * How long a drag settles before the working set is written to localStorage, in
 * milliseconds. A drag reports once per animation frame; stringifying and
 * writing the whole trajectory that often is real jank on a phone, so the write
 * trails the gesture instead. Every other mutation persists synchronously.
 */
export const DRAG_SAVE_DELAY = 250

/**
 * How long a transient button label — "Copied", "Couldn't copy", "Export
 * failed" — stays before the button returns to its idle text, in milliseconds.
 * Exported so tests can advance it with fake timers.
 */
export const LABEL_FEEDBACK_DURATION = 2000

/** Matches a share parameter in the fragment without interpreting its payload. */
const SHARE_PARAM_PATTERN = new RegExp(`(?:^|[#&])${SHARE_PARAM}=`)

/**
 * The one thing the old message banner still has to say. A share link that
 * cannot be decoded falls back to localStorage, and a recipient would otherwise
 * silently see someone else's trajectory (or an empty app) with no explanation.
 */
const SHARE_READ_ERROR =
  'That share link could not be read, so this is what is saved in this browser.'

/**
 * Year the tap dialog proposes: the latest entry's year + 1, or the current
 * year for an empty trajectory.
 *
 * Clamped through the same `isValidYear` the dialog validates with, so a
 * trajectory ending at 2999 proposes 2999 instead of the invalid 3000 — the
 * default must be a year Confirm would accept, or the dialog would open on a
 * value it refuses.
 */
function defaultYearFor(entries: Entry[]): number {
  if (entries.length === 0) return new Date().getFullYear()
  const latest = entries.reduce((max, entry) => Math.max(max, entry.year), entries[0].year)
  const proposed = latest + 1
  if (isValidYear(proposed)) return proposed
  return proposed < 1000 ? 1000 : 2999
}

interface InitialState {
  entries: Entry[]
  /** True when the URL carried a share parameter that could not be read. */
  shareUnreadable: boolean
  /** True when the entries came from a share link and still have to be saved. */
  fromShare: boolean
}

/**
 * Resolve the starting entries and whether the URL's share link was readable.
 * Pure: it reads the URL and localStorage but never writes, so `StrictMode`
 * double-invoking it cannot leave storage holding an array React discarded.
 *
 * A `d` parameter that reads back into entries is the source of truth and is
 * persisted by the caller, so a reload keeps it. A `d` parameter that is present
 * but unreadable is reported on the page and the localStorage working set is used
 * instead — a genuinely empty payload decodes to `[]`, which is a valid empty
 * app, not a broken link. Without a parameter the URL says nothing about the
 * entries and localStorage is the working set.
 */
function resolveInitialState(): InitialState {
  const hash = window.location.hash
  if (!SHARE_PARAM_PATTERN.test(hash)) {
    return { entries: loadEntries(), shareUnreadable: false, fromShare: false }
  }

  const shared = readShareFromHash(hash)
  if (shared === null) {
    return { entries: loadEntries(), shareUnreadable: true, fromShare: false }
  }

  return { entries: shared, shareUnreadable: false, fromShare: true }
}

/**
 * Copy text to the clipboard, falling back to the legacy textarea trick.
 *
 * `navigator.clipboard` needs a secure context and a user gesture and rejects in
 * plenty of browsers, so a missing API *or* a rejected promise both fall through
 * to `document.execCommand('copy')`. Returns `false` only when neither worked,
 * which is the caller's cue to report the failure in the button it came from.
 */
async function copyToClipboard(text: string): Promise<boolean> {
  const clipboard: Clipboard | undefined = navigator.clipboard
  if (clipboard !== undefined && typeof clipboard.writeText === 'function') {
    try {
      await clipboard.writeText(text)
      return true
    } catch {
      // Fall through to the legacy path.
    }
  }

  const exec = document.execCommand
  if (typeof exec !== 'function') return false

  const textarea = document.createElement('textarea')
  textarea.value = text
  textarea.setAttribute('readonly', '')
  // Off screen but still focusable/selectable, which is what `select` and the
  // copy command need.
  textarea.style.position = 'fixed'
  textarea.style.top = '-1000px'
  textarea.style.opacity = '0'
  document.body.append(textarea)
  try {
    textarea.select()
    // Called on `document`: detaching the method loses its receiver and throws
    // `Illegal invocation` in every browser.
    return document.execCommand('copy')
  } catch {
    return false
  } finally {
    textarea.remove()
  }
}

export default function App(): ReactElement {
  const [initial] = useState(resolveInitialState)
  const [entries, setEntries] = useState<Entry[]>(initial.entries)
  const [focusAfterDelete, setFocusAfterDelete] = useState<EntryFocusTarget | null>(null)
  /** The Copy link button's own label state. */
  const [copyStatus, setCopyStatus] = useState<CopyStatus>('idle')
  /** The Download PNG button's own label state. */
  const [downloadStatus, setDownloadStatus] = useState<DownloadStatus>('idle')
  /**
   * The point the dialog is open for, or `null` when no dialog is up: a create
   * at a coordinate (a chart tap or Add point) or an entry being edited. Every
   * path into the dialog is one of these, and the dialog cannot outlive it.
   */
  const [dialogTarget, setDialogTarget] = useState<PointDialogTarget | null>(null)
  const svgRef = useRef<SVGSVGElement>(null)
  /**
   * Pending storage write for a drag, guarded so a late timer cannot outlive
   * the values it was scheduled for.
   */
  const dragSaveTimerRef = useRef<number | null>(null)
  /** The latest entries, so the trailing write persists what is on screen. */
  const entriesRef = useRef(entries)
  /** Pending reverts for the two transient button labels. */
  const copyTimerRef = useRef<number | null>(null)
  const downloadTimerRef = useRef<number | null>(null)

  useEffect(() => {
    entriesRef.current = entries
  }, [entries])

  /** Cancel any pending label revert. */
  function clearFeedbackTimers(): void {
    if (copyTimerRef.current !== null) {
      window.clearTimeout(copyTimerRef.current)
      copyTimerRef.current = null
    }
    if (downloadTimerRef.current !== null) {
      window.clearTimeout(downloadTimerRef.current)
      downloadTimerRef.current = null
    }
  }

  /**
   * A new action clears whatever the last one left on a button — the app's
   * convention that a stale message never survives the next action. An export
   * that is still in flight is deliberately left alone: `preparing` is not a
   * result, and reverting it would re-enable the button mid-export.
   */
  function clearStaleFeedback(): void {
    clearFeedbackTimers()
    setCopyStatus('idle')
    setDownloadStatus((previous) => (previous === 'failed' ? 'idle' : previous))
  }

  /** Show a copy result in the button and revert it after a moment. */
  function showCopyResult(result: 'copied' | 'failed'): void {
    if (copyTimerRef.current !== null) window.clearTimeout(copyTimerRef.current)
    setCopyStatus(result)
    copyTimerRef.current = window.setTimeout(() => {
      copyTimerRef.current = null
      setCopyStatus('idle')
    }, LABEL_FEEDBACK_DURATION)
  }

  // A pending label revert must not outlive the app.
  useEffect(() => clearFeedbackTimers, [])

  /** Write the working set now and drop any pending drag save. */
  function flushDragSave(): void {
    if (dragSaveTimerRef.current === null) return
    window.clearTimeout(dragSaveTimerRef.current)
    dragSaveTimerRef.current = null
    saveEntries(entriesRef.current)
  }

  /** Coalesce a drag's per-frame updates into one trailing write. */
  function scheduleDragSave(): void {
    if (dragSaveTimerRef.current !== null) window.clearTimeout(dragSaveTimerRef.current)
    dragSaveTimerRef.current = window.setTimeout(() => {
      dragSaveTimerRef.current = null
      saveEntries(entriesRef.current)
    }, DRAG_SAVE_DELAY)
  }

  // A tab can go away mid-drag, so the trailing write is flushed on unmount and
  // when the page is hidden or unloaded: nothing is lost if the drag is the
  // last thing that happened.
  useEffect(() => {
    const handleHidden = (): void => flushDragSave()
    window.addEventListener('pagehide', handleHidden)
    document.addEventListener('visibilitychange', handleHidden)
    return () => {
      window.removeEventListener('pagehide', handleHidden)
      document.removeEventListener('visibilitychange', handleHidden)
      flushDragSave()
    }
  }, [])

  // A share link is the source of truth for the entries it carries, so persist
  // them once after mount (writing during render is not allowed and StrictMode
  // would run it twice). The array saved is the very one in state, so the ids in
  // storage and on screen can never disagree.
  useEffect(() => {
    if (initial.fromShare) saveEntries(initial.entries)
  }, [initial])

  // Mirror the entries into the URL fragment, debounced so a burst of
  // keystrokes rewrites it once. `replaceState` never adds a history entry, a
  // correct URL is left alone, and the timeout is cleared on unmount. The state
  // this reads is already resolved (it was built in the initializer), so the
  // fragment is never written from an unresolved app.
  useEffect(() => {
    const timeout = window.setTimeout(() => {
      const next = buildShareUrl(entries)
      if (next !== window.location.href) window.history.replaceState(null, '', next)
    }, SHARE_SYNC_DELAY)

    return () => window.clearTimeout(timeout)
  }, [entries])

  /**
   * Replace the entries and persist them immediately; every mutation except a
   * drag goes through here (a drag trails its write, see `scheduleDragSave`).
   */
  function commit(next: Entry[]): void {
    setEntries(next)
    saveEntries(next)
  }

  /**
   * Add point opens the dialog at the origin with the default year. A keyboard
   * or screen-reader user cannot tap the chart, and the dialog lets them type
   * any coordinate, so this is a complete path to any point.
   */
  function handleAddPoint(): void {
    // A new action clears the previous action's button feedback, the app's
    // convention.
    clearStaleFeedback()
    setDialogTarget({ mode: 'create', x: 0, y: 0, year: defaultYearFor(entries) })
  }

  /**
   * Delete the entry the dialog is editing, then move focus where the old row
   * deletion did: the legend row that slides into the deleted row's place in the
   * rendered, chronologically sorted order, or the "Add point" button when none
   * is left. Focus must never fall back to `<body>`.
   */
  function handleDelete(): void {
    const target = dialogTarget
    if (target === null || target.mode !== 'edit') return

    clearStaleFeedback()
    const id = target.entry.id
    const sorted = sortChronologically(entries)
    const index = sorted.findIndex((entry) => entry.id === id)
    const remaining = sorted.filter((entry) => entry.id !== id)
    const replacement = index === -1 ? undefined : remaining[Math.min(index, remaining.length - 1)]

    commit(entries.filter((entry) => entry.id !== id))
    setDialogTarget(null)
    setFocusAfterDelete({ rowId: replacement?.id ?? null })
    // Deliberately silent: the row disappearing is the feedback.
  }

  function handleClearAll(): void {
    clearStaleFeedback()
    if (!window.confirm('Delete all entries? This cannot be undone.')) return
    commit([])
    // Deliberately silent: the empty chart is the feedback.
  }

  /**
   * A tap on open plot proposes a **new** point there: the dialog opens at the
   * tapped coordinate with the default year, and nothing is written until it is
   * saved, which is what makes a stray tap cost nothing.
   */
  function handlePlacePoint(x: number, y: number): void {
    // A new action clears the previous action's button feedback, the app's
    // convention: no stale "Copied" may survive behind the dialog.
    clearStaleFeedback()
    setDialogTarget({ mode: 'create', x, y, year: defaultYearFor(entries) })
  }

  /** A tap on a dot opens that entry for editing. */
  function handleEditPoint(id: string): void {
    const entry = entries.find((candidate) => candidate.id === id)
    if (entry === undefined) return
    clearStaleFeedback()
    setDialogTarget({ mode: 'edit', entry })
  }

  /**
   * A drag calls this once per animation frame, so it keeps the entry live on
   * screen but lets the storage write trail the gesture (see
   * {@link DRAG_SAVE_DELAY}). Its year and description are left exactly as they
   * were, and it deliberately reports nothing — a message per frame would be
   * noise.
   */
  function handleMovePoint(id: string, x: number, y: number): void {
    // Functional update: the gesture's rAF callback can fire between renders,
    // so mapping over this render's snapshot could drop another change.
    setEntries((prev) => prev.map((entry) => (entry.id === id ? { ...entry, x, y } : entry)))
    scheduleDragSave()
  }

  /**
   * Save the dialog: append a created point, or replace the edited entry in
   * place keeping its id. Both persist immediately through `commit`, and neither
   * reports anything — the dot appearing (or moving) is its own feedback.
   */
  function handleSave(values: PointDialogValues): void {
    const target = dialogTarget
    if (target === null) return

    // Clear an older action's button feedback (the app's convention) but
    // deliberately set none of its own: a confirmation for something the user
    // just watched appear is noise.
    clearStaleFeedback()
    if (target.mode === 'create') {
      commit([
        ...entries,
        createEntry({
          x: values.x,
          y: values.y,
          year: values.year,
          description: values.description,
        }),
      ])
    } else {
      commit(
        entries.map((entry) =>
          entry.id === target.entry.id
            ? {
                ...entry,
                x: values.x,
                y: values.y,
                year: values.year,
                description: values.description,
              }
            : entry,
        ),
      )
    }

    setDialogTarget(null)
    // Deliberately no focus move for a chart-placed point. Placing points is a
    // rhythm, and focusing the legend would yank the viewport down out of the
    // chart because a browser scrolls to whatever it focuses. "Add point" is
    // the opposite case: it is a request to type, and the dialog it opens takes
    // focus on the year field.
  }

  /** Cancel and Escape are the same path: close the dialog, change nothing. */
  function handleCancel(): void {
    setDialogTarget(null)
  }

  /**
   * Copy the share link. The result lands in the button's own label — a
   * checkmark and "Copied", or "Couldn't copy" — because `copyToClipboard`
   * already contains the whole fallback chain and there is nowhere better for
   * the user to see which half of it ran.
   *
   * A link that goes over `SHARE_LENGTH_WARNING` is copied like any other and
   * no longer warns: the copy has already happened, so the length is something
   * the user can only act on by removing entries, and the app's only remaining
   * notice is the button it was just pressed on.
   */
  async function handleCopyLink(): Promise<void> {
    clearStaleFeedback()
    if (entries.length === 0) return
    const url = buildShareUrl(entries)
    showCopyResult((await copyToClipboard(url)) ? 'copied' : 'failed')
  }

  async function handleDownloadPng(): Promise<void> {
    clearStaleFeedback()
    const svg = svgRef.current
    if (svg === null || entries.length === 0) return

    setDownloadStatus('preparing')
    try {
      const blob = await renderCompassToPng(svg, { scale: 2, entries })
      // The file is named after the trajectory's own span, not today's date: a
      // downloads folder cannot tell two histories apart by the day they were
      // exported, and the years are exactly what does. `entries` is non-empty
      // here, so both ends of the span exist. A single entry names its own year.
      const chronological = sortChronologically(entries)
      downloadBlob(
        blob,
        exportFilename(new Date(), {
          from: chronological[0].year,
          to: chronological[chronological.length - 1].year,
        }),
      )
      // The file landing in the downloads folder is the feedback, so the label
      // simply returns to idle: a success message here would be noise.
      setDownloadStatus('idle')
    } catch (error) {
      // The banner that used to carry the renderer's own message is gone, so a
      // failure now says so in the button that started it, for the same moment
      // "Copied" gets. The underlying error is not swallowed: it goes to the
      // console, where the ErrorBoundary's own report also goes.
      console.error('Chart export failed', error)
      setDownloadStatus('failed')
      downloadTimerRef.current = window.setTimeout(() => {
        downloadTimerRef.current = null
        setDownloadStatus('idle')
      }, LABEL_FEEDBACK_DURATION)
    }
  }

  return (
    <div className="app">
      <header className="app__header">
        <h1 className="app__title">Political Compass Trajectory</h1>
        {/* The author's credit, a subtitle directly under the title. It is the
            app's first and only external link, so it carries
            `target="_blank"` with `rel="noreferrer noopener"`: a new tab must
            not get a handle on `window.opener`. The exported PNG deliberately
            carries no credit — that image is the user's own artifact, not a
            share of this page. */}
        <p className="app__credit">
          Created by{' '}
          <a href="https://x.com/libertypenguin0" target="_blank" rel="noreferrer noopener">
            @libertypenguin0
          </a>
        </p>
        <p className="app__lede">
          Chart how a political outlook drifts over time. The <strong>x-axis</strong> runs from -10
          (socialism) to +10 (free market); the <strong>y-axis</strong> runs from -10 (libertarian)
          to +10 (authoritarian).
        </p>
        {/* An explicit role: `list-style: none` makes WebKit drop the implicit
            list role, which would lose the announced item count. Same fix as
            the legend below. */}
        <ul className="quadrant-key" role="list" aria-label="Quadrant colours">
          {QUADRANT_ORDER.map((key) => (
            <li key={key} className="quadrant-key__item">
              <span
                className="quadrant-key__swatch"
                style={{ backgroundColor: PALETTE[key] }}
                aria-hidden="true"
              />
              <span>{QUADRANT_LABELS[key]}</span>
            </li>
          ))}
        </ul>
      </header>

      {/* There is deliberately no notification slot here any more: the two
          actions that can fail or take a moment report in their own buttons
          (see Toolbar), and clearing or deleting report through the list
          changing. Nothing a toast used to push the cards down for is left. */}
      <div className="app__layout">
        {/* `compass-card` in addition to `card`: on a phone the compass drops the
            card chrome and goes full-bleed (see the max-width: 767px block in
            app.css), and only this section may do that. */}
        <section
          className="card compass-card"
          aria-labelledby="compass-heading"
          aria-describedby={
            initial.shareUnreadable ? 'chart-instructions share-error' : 'chart-instructions'
          }
        >
          <h2 className="card__title" id="compass-heading">
            Trajectory
          </h2>
          {/* The only thing telling a first-time visitor that both the chart
              and the legend below it are usable, and it names all three
              gestures: place, edit and drag. It sits with the chart, above it,
              and is part of the card's accessible description. */}
          <p
            className="compass-card__instructions"
            id="chart-instructions"
            data-testid="chart-instructions"
          >
            Tap or click the chart to add a point. Tap or click a dot or a legend row to edit it,
            or drag a dot to move it.
          </p>
          {/* The one deliberate survivor of the old banner. A share link that
              cannot be read falls back to localStorage, and without this line a
              recipient would silently see someone else's trajectory, or an empty
              app, with no explanation at all. It is page state, not a
              notification: rendered once from the resolved initial state, with
              no dismissal and no live region to announce it. */}
          {initial.shareUnreadable ? (
            <p className="compass-card__share-error" id="share-error" data-testid="share-error">
              {SHARE_READ_ERROR}
            </p>
          ) : null}
          <div className="compass-card__viewport">
            {/* The chart is laid out at this element's width and never scrolls
                or overflows; the wrapper exists to anchor the empty-state
                overlay below. */}
            <div className="compass-card__canvas">
              <CompassChart
                entries={entries}
                svgRef={svgRef}
                onPlacePoint={handlePlacePoint}
                onEditPoint={handleEditPoint}
                onMovePoint={handleMovePoint}
              />
            </div>
            {/* An HTML overlay, not an SVG hint: it is a page affordance, not
                part of the picture, so it must not appear in an export. */}
            {entries.length === 0 && (
              <div className="compass-empty" data-testid="compass-empty">
                <p className="compass-empty__title">The compass is empty</p>
                <p>Tap anywhere on the chart to plot your first year.</p>
              </div>
            )}
          </div>

          <Toolbar
            canDownloadPng={entries.length > 0}
            canCopyLink={entries.length > 0}
            downloadStatus={downloadStatus}
            copyStatus={copyStatus}
            onDownloadPng={() => {
              void handleDownloadPng()
            }}
            onCopyLink={() => {
              void handleCopyLink()
            }}
            onClearAll={handleClearAll}
          />
        </section>

        <EntryList
          entries={entries}
          focusAfterDelete={focusAfterDelete}
          onAdd={handleAddPoint}
          onEdit={handleEditPoint}
        />
      </div>

      {/* The single editing surface. `target` is the only open/closed signal,
          and every callback clears it, so the dialog cannot outlive the action
          that opened it. It renders nothing while `target` is null. */}
      <PointDialog
        target={dialogTarget}
        onSave={handleSave}
        onDelete={handleDelete}
        onCancel={handleCancel}
      />
    </div>
  )
}
